import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFollowSelectedVehicle, getIsFullMissionRouteVisible, getSelectedVehicle } from 'store/slices/appSlice';

import type { VehicleV2 } from 'API';

const useCenterVehicle = (vehicle?: VehicleV2) => {
    const isVehicleFocused = useSelector(getFollowSelectedVehicle);
    const selectedVehicleState = useSelector(getSelectedVehicle);
    const hasMission = useSelector(getIsFullMissionRouteVisible);
    const position = vehicle?.position?.value?.position;
    const latitude = position?.latitude;
    const longitude = position?.longitude;
    const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number';

    const pvlTriggered = !!selectedVehicleState?.pvlTriggered;
    const selectedVIN = selectedVehicleState?.selectedVIN;

    // Continuous re-centring belongs to the lock alone.
    const shouldFollow = !hasMission && isVehicleFocused;

    const [focusRequest, setFocusRequest] = useState(0);
    const [servedRequest, setServedRequest] = useState(0);
    const previousVIN = useRef(selectedVIN);
    const wasLocked = useRef(isVehicleFocused);

    useEffect(() => {
        const vinChanged = selectedVIN !== previousVIN.current;
        const lockEngaged = isVehicleFocused && !wasLocked.current;
        previousVIN.current = selectedVIN;
        wasLocked.current = isVehicleFocused;

        // While the full mission route is shown, that view owns the viewport.
        if (hasMission || !selectedVIN) {
            return;
        }

        // A new selection focuses only when it came from the search box or the sidebar, so
        // clicking a marker on the map selects without moving the viewport.
        if (lockEngaged || (vinChanged && (pvlTriggered || isVehicleFocused))) {
            setFocusRequest(request => request + 1);
        }
    }, [selectedVIN, isVehicleFocused, pvlTriggered, hasMission]);

    // Hold the request until a position is actually known, so a focus is never wasted on a
    // vehicle whose telemetry has not arrived yet.
    const isFocusing = focusRequest > servedRequest && hasCoordinates;

    useEffect(() => {
        if (isFocusing) {
            setServedRequest(focusRequest);
        }
    }, [isFocusing, focusRequest]);

    const center = useMemo(
        () =>
            (shouldFollow || isFocusing) && typeof latitude === 'number' && typeof longitude === 'number'
                ? { lat: latitude, lng: longitude }
                : undefined,
        [shouldFollow, isFocusing, latitude, longitude]
    );

    const reset = () => {
        setServedRequest(focusRequest);
    };

    return { center, reset };
};

export default useCenterVehicle;
